<template>
  <div class="mb-main__horarios px-16 pt-20 pb-100 mb-100 min-h-screen">
    <div class="mb-horarios__horaopts flex justify-between mb-36">
      <FechaHora />

      <div class="flex items-center gap-8">
        <LoadingHorarios
          :loaded="loaded.horarios"
          :could-sync="horarios.couldSync"
        />
        <ButtonDarkLightMode />
      </div>
    </div>

    <div>
      <h1 class="sr-only">
        Horarios y tarifas servicio {{ servicios.servicioActivo?.name
        }}{{
          estacionSalida?.name ? ` desde estación ${estacionSalida?.name}` : ''
        }}
        {{
          estacionLlegada?.name
            ? ` hasta estación ${estacionLlegada?.name}`
            : ''
        }}
      </h1>
    </div>

    <div class="estaciones_servicios items-end flex mb-24">
      <h2 class="text-base font-semibold leading-none mr-8 mb-8.5">
        Servicio
        <span class="sr-only" v-if="servicios.servicioActivo?.name">{{
          ` ${servicios.servicioActivo?.name}` || ''
        }}</span>
      </h2>
      <SelectServicio
        :servicios="servicios.servicios"
        :servicio-base="servicio"
        @servicio="cambiarServicio"
        @star="(srv: any) => srv._id"
      />
    </div>

    <div class="mb-estaciones__filtro-opciones flex justify-end mb-16 gap-4">
      <button
        type="button"
        :class="['mb-btn ml-4', geo.isGettingGeo && 'buscando']"
        data-test="geo-btn"
        @click="getSetGeo"
      >
        {{ geo.isGettingGeo ? 'Buscando' : 'Encontrar' }}
        <LucideMapPin :size="16" aria-hidden="true" />
      </button>
      <button
        type="button"
        class="mb-btn stroke-current fill-none"
        data-test="invert-btn"
        @click="invertirRuta"
      >
        Invertir
        <LucideArrowUpDown :size="16" aria-hidden="true" />
      </button>
    </div>

    <SelectEstaciones
      class="mb-20"
      tipo="Salida"
      :estaciones-base="estaciones"
      :estacion-id="String(estacionSalida?._id)"
      :estaciones-user="user.estaciones"
      @estacion="(est: any) => horarios.cambiarRuta({ salida: est?._id })"
      @star="toggleStarEstacion"
    />

    <SelectEstaciones
      class="mb-28"
      tipo="Llegada"
      :estaciones-base="estaciones"
      :estacion-id="String(estacionLlegada?._id)"
      :estaciones-user="user.estaciones"
      @estacion="(est) => horarios.cambiarRuta({ llegada: est?._id })"
      @star="toggleStarEstacion"
    />

    <div v-if="tarifas && tarifas.tarifas" class="">
      <h2 class="sr-only">
        Tarifas
        {{ estacionSalida?.name ? ` ${estacionSalida?.name}` : '' }}
        {{ estacionLlegada?.name ? ` - ${estacionLlegada?.name}` : '' }}
      </h2>
      <div class="mb-horarios__tarifas-container grid grid-cols-3 mb-12">
        <div
          class="mb-tarifa__saldo"
          v-for="(user, iuser) in tarifas.usuarios"
          :key="user"
        >
          <p class="text-center text-xs leading-none uppercase">{{ user }}</p>
          <h3>
            <small class="text-sm">$</small>
            {{ tarifas.tarifas[iuser] }}
          </h3>
        </div>
      </div>
    </div>

    <LimiteEstaciones :limite="user.limite" @toggle-limite="toggleLimite" />

    <div class="mb-50 mb-horarios__container" data-testid="horarios-container">
      <h2 class="sr-only">Horarios disponibles</h2>
      <div
        v-if="servicios.isFeriado"
        class="mb-error text-xl my-24 text-center rounded-sm p-20 bg-orange text-black shadow border-orange min-h-[56px]"
        role="alert"
      >
        <strong>Hoy es feriado</strong> y el servicio no pasa por hoy.
      </div>

      <div
        v-if="horariosError"
        class="mb-error text-xl mb-8 mt-24 text-red-600 mb-8"
      >
        Error al cargar horarios. Intenta de nuevo.
      </div>

      <template v-else>
        <ListaHorarios
          v-if="estacionSalidaParam && estacionLlegadaParam"
          :horarios="horariosLista"
          :horarios-vuelta="horariosVuelta"
          :estacion-salida="estacionSalida"
          :estacion-llegada="estacionLlegada"
          :limite="user.limite"
          :fecha="fecha"
          :is-loading-horarios="horarios.loading"
          :is-feriado="servicios.isFeriado"
        />
        <LazyVisibleListaHorarios
          v-else
          v-memo
          hydrate-on-visible
          :horarios="horariosLista"
          :horarios-vuelta="horariosVuelta"
          :estacion-salida="estacionSalida"
          :estacion-llegada="estacionLlegada"
          :limite="user.limite"
          :fecha="fecha"
          :is-loading-horarios="horarios.loading"
          :is-feriado="servicios.isFeriado"
          :is-first-run="horarios.firstRun"
        />
      </template>
    </div>

    <ClientOnly>
      <LazyVisibleShareHorario
        v-if="estacionSalida?._id && estacionLlegada?._id"
        hydrate-on-visible
        :salida="estacionSalida"
        :llegada="estacionLlegada"
        :share-data="shareData"
      />
    </ClientOnly>
  </div>
</template>

<script lang="ts" setup>
import { getEstacionByGeo } from '~~/helpers';
import type { IEstacion, IServicio } from '~~/interfaces';

import { useHorarioStore } from '~~/stores/horarios';
import { useServiciosStore } from '~~/stores/servicios';
import { useUserStore } from '~~/stores/userconfig';
import { geoStore } from '~~/stores/geolocation';
import { horaState } from '~~/stores/hora';
import { useNotificacionesStore } from '~~/stores/notificaciones';
import {
  BASE_SERVICIO_ID,
  BASE_SERVICIO_SLUG,
  BASE_SITE,
  type TServiciosSlug,
} from '~~/constants';
import { formatToISO8601, durationToISO8601 } from '~~/helpers/hora';
import { buildSavedStationRoute } from '~~/helpers/preferences';
import { usePageSeo } from '~~/composables/usePageSeo';
import { buildSeoMeta } from '~~/helpers/seo';
import {
  buildTripSchema,
  buildScheduleListSchema,
  buildTransitMapSchema,
  buildBreadcrumbSchema,
} from '~~/helpers/schema';

const route = useRoute();
const router = useRouter();
const config = useRuntimeConfig();

const servicios = useServiciosStore();
const horarios = useHorarioStore();
const user = useUserStore();
const geo = geoStore();
const hora = horaState();
const notifs = useNotificacionesStore();
const savedStationRouteApplied = ref(false);

const servicioParam = computed<TServiciosSlug>(
  () => String(route.params.servicio) as TServiciosSlug
);
const estacionSalidaParam = computed(
  () => router.currentRoute.value.params.estaciones?.[0] || ''
);
const estacionLlegadaParam = computed(
  () => router.currentRoute.value.params.estaciones?.[1] || ''
);

let nuevaRuta: { salida: string; llegada: string };
const estacionSalidaRoute = computed(() =>
  servicios.estaciones.get(estacionSalidaParam.value)
);
const estacionLlegadaRoute = computed(() =>
  servicios.estaciones.get(estacionLlegadaParam.value)
);

await useAsyncData(`${servicioParam.value}:tarifas`, () => {
  return servicios
    .populateServicios(true)
    .then(() =>
      Promise.all([
        horarios.fetchTarifas(servicios.servicioId || BASE_SERVICIO_ID),
      ]).catch(console.error)
    );
});

// definePageMeta({
//   scrollToTop: false,
// });

await callOnce(servicioParam.value, async () => {
  const servicio = servicios.existeServicio(servicioParam.value);

  if (!servicio) router.replace('/');

  if (servicio?._id !== servicios.servicioActivo?._id) {
    servicios.cambiarServicio({ servicio: servicio?._id });
  }

  const salida = servicios.estacionBaseSalidaLlegada.salida?._id;
  const llegada = servicios.estacionBaseSalidaLlegada.llegada?._id;

  if (estacionSalidaParam.value) {
    if (!estacionSalidaRoute.value) {
      return router.replace(`/${servicio?.slug || BASE_SERVICIO_SLUG}`);
    }
  }

  if (estacionLlegadaParam.value) {
    if (!estacionLlegadaRoute.value)
      return router.replace(
        `/${servicio?.slug}/${estacionSalidaParam.value || ''}`
      );
  }

  nuevaRuta = Object.assign(
    {},
    estacionSalidaRoute.value
      ? { salida: estacionSalidaRoute.value?._id }
      : { salida },
    estacionLlegadaRoute.value
      ? { llegada: estacionLlegadaRoute.value?._id }
      : { llegada }
  );

  if (estacionSalidaRoute.value && estacionLlegadaRoute.value) {
    await horarios
      .getHorario(Object.assign({}, nuevaRuta, { external: true }))
      .catch((e: unknown) => console.log('getHorario', e));
  }
});

onMounted(async () => {
  await Promise.all([
    servicios.initDB(),
    notifs.getNotificaciones(servicios.servicioId || BASE_SERVICIO_ID),
  ]);

  await servicios.populateServicios(true);

  const servicioUser = user.servicio;

  if (servicioUser && servicioUser !== servicioParam.value) {
    const servicioUserFull = servicios.servicios.get(servicioUser);

    if (servicioParam.value === servicioUserFull?.slug) {
      servicios.cambiarServicio({ servicio: servicioUser });
    }
  }

  await nextTick();
  if (await reconcileSavedStationRoute()) {
    return;
  }

  if (!estacionSalidaParam.value) {
    await geo.autoGetGeo();

    let estSalidaGeo: IEstacion | undefined;

    if (geo.coords) {
      estSalidaGeo = getEstacionByGeo(
        Array.from(servicios.estaciones.values())
      )(geo.coords);
    }
    const { llegada } = servicios.estacionBaseSalidaLlegada;

    if (estSalidaGeo && geo.hasGeolocationPermitted()) {
      horarios.cambiarRuta({
        salida: estSalidaGeo?._id,
        llegada: llegada?._id,
        cambiarRuta: false,
      });
    } else if (
      estacionesUsuarioService.value?.length &&
      estacionesUsuarioService.value.length >= 1
    ) {
      const estacionUsuario1 = estacionesUsuarioService.value[0];
      const estacionUsuario2 = estacionesUsuarioService.value[1];

      horarios.cambiarRuta({
        salida: estacionUsuario1,
        llegada: estacionUsuario2 || geo.baseEstacion?._id,
        cambiarRuta: false,
      });
    } else {
      horarios.getHorario().catch((e: unknown) => console.log(e));
    }
  }
});

watch(
  [() => user.estacionesServicio, () => servicios.estaciones.size],
  () => {
    void reconcileSavedStationRoute();
  },
  { deep: true }
);

const getSavedStationRoute = (): string | null => {
  if (!import.meta.client) return null;
  if (estacionSalidaParam.value || estacionLlegadaParam.value) return null;

  const target = buildSavedStationRoute(
    user.estacionesServicio,
    servicioParam.value,
    Array.from(servicios.estaciones.values())
  );

  if (!target || target === router.currentRoute.value.path) return null;
  return target;
};

const reconcileSavedStationRoute = async (): Promise<boolean> => {
  if (savedStationRouteApplied.value) return false;

  const target = getSavedStationRoute();
  if (!target) return false;

  savedStationRouteApplied.value = true;
  const [salidaSlug, llegadaSlug] = target.split('/').slice(-2);
  if (!salidaSlug || !llegadaSlug) {
    savedStationRouteApplied.value = false;
    return false;
  }
  const estacionSalida = servicios.estaciones.get(salidaSlug);
  const estacionLlegada = servicios.estaciones.get(llegadaSlug);

  if (!estacionSalida || !estacionLlegada) {
    savedStationRouteApplied.value = false;
    return false;
  }

  await horarios.cambiarRuta({
    salida: String(estacionSalida._id),
    llegada: String(estacionLlegada._id),
    cambiarRuta: false,
  });
  await router.replace(target);
  return true;
};

const shareData = computed(() => {
  return {
    title: seoMeta.value.title,
    text: seoMeta.value.description,
    url: `https://mibiotren.cl/${servicios.servicioActivo?.slug}/${horarios.estacionSalida?.slug}/${horarios.estacionLlegada?.slug}`,
  };
});

const fecha = computed(() => hora.date);
const toggleLimite = (value = 0) => {
  user.setPreferencia({ key: 'limite', value });
};
const toggleStarEstacion = (estacion: IEstacion) => {
  if (!estacion) return;
  user.saveEstacion(String(estacion._id), servicios.servicioActivo?.slug);
};

const getSetGeo = async () => {
  const coords = await geo.getSetGeo().catch(() => undefined);
  if (!coords) return;

  const estacion = getEstacionByGeo(
    Array.from(servicios.estaciones.values() || [])
  )(coords.value);

  if (estacion) {
    await horarios.cambiarRuta({ salida: String(estacion._id) });
  }
};

const loaded = computed(() => ({
  horarios: horarios.initLoad,
}));
const estaciones = computed(() => servicios.estaciones);
// TODO: FIX ERROR
// @ts-ignore-error
const horariosError = computed(() => horarios.error);

const horariosLista = computed(() => horarios.horarios);
const estacionSalida = computed(() => horarios.estacionSalida);
const estacionLlegada = computed(() => horarios.estacionLlegada);
const invertirRuta = async () => {
  const currentSalida = horarios.estacionSalida?.slug;
  const currentLlegada = horarios.estacionLlegada?.slug;

  if (currentSalida && currentLlegada) {
    await navigateTo(
      `/${servicios.servicio}/${currentLlegada}/${currentSalida}`,
      { replace: true }
    );

    horarios.cambiarRuta({
      salida: String(horarios.estacionLlegada?._id),
      llegada: String(horarios.estacionSalida?._id),
    });
  }
};

const servicio = computed(() => servicios.servicio);
const horariosVuelta = computed(() => horarios.horariosVueltaMap);
const estacionesUsuarioService = computed(
  () => user.estacionesServicio[servicio.value]
);

const tarifas = computed(() => {
  return horarios.tarifas;
});

const cambiarServicio = async (servicio: IServicio) => {
  await user.selectServicio(servicio.slug);
};

const seoMeta = computed(() =>
  buildSeoMeta({
    serviceName: servicios.servicioActivo?.name,
    serviceSlug: servicios.servicioActivo?.slug,
    departureStationName: estacionSalidaRoute.value?.name,
    departureStationSlug: estacionSalidaRoute.value?.slug,
    arrivalStationName: estacionLlegadaRoute.value?.name,
    arrivalStationSlug: estacionLlegadaRoute.value?.slug,
    servicioParam: servicioParam.value,
  })
);

usePageSeo();

useSeoMeta(seoMeta.value);

useHead(() => {
  const script: {
    type: 'application/ld+json';
    innerHTML: string;
    key: string;
  }[] = [];

  if (estacionSalida?.value && estacionLlegada?.value) {
    script.push({
      type: 'application/ld+json',
      innerHTML: JSON.stringify(
        buildTripSchema({
          serviceName: servicios.servicioActivo?.name || 'Biotren',
          estacionSalida: {
            name: estacionSalida.value.name,
            lat: estacionSalida.value.lat,
            lng: estacionSalida.value.lng,
          },
          estacionLlegada: {
            name: estacionLlegada.value.name,
            lat: estacionLlegada.value.lat,
            lng: estacionLlegada.value.lng,
          },
          tarifa: tarifas.value?.tarifas?.[0] as number | undefined,
          duracion: horarios.horarios[0]?.duracion,
        })
      ),
      key: 'org-schema',
    });

    if (horarios.horarios.length) {
      script.push({
        type: 'application/ld+json',
        innerHTML: JSON.stringify(
          buildScheduleListSchema({
            estacionSalidaName: estacionSalida.value.name,
            estacionLlegadaName: estacionLlegada.value.name,
            horarios: horarios.horarios,
          })
        ),
        key: 'schedules-schema',
      });
    }
  }

  if (servicios.estaciones.size) {
    const estacionesList = Array.from(
      servicios.estaciones.values(),
      (est) => est.name
    );

    script.push({
      type: 'application/ld+json',
      innerHTML: JSON.stringify(
        buildTransitMapSchema({
          serviceName: servicios.servicioActivo?.name,
          servicioSlug: servicioParam.value,
          estaciones: estacionesList,
          siteUrl: config.public.siteUrl as string,
        })
      ),
      key: 'org-schema-estaciones',
    });
  }

  const breadcrumbItems: { name: string; url: string }[] = [
    { name: 'Inicio', url: BASE_SITE.URL },
    {
      name: servicios.servicioActivo?.name || 'Servicio',
      url: `${BASE_SITE.URL}/${servicioParam.value}`,
    },
  ];

  if (estacionSalida?.value && estacionLlegada?.value) {
    breadcrumbItems.push({
      name: `${estacionSalida.value.name} a ${estacionLlegada.value.name}`,
      url: `${BASE_SITE.URL}/${servicioParam.value}/${estacionSalida.value.slug}/${estacionLlegada.value.slug}`,
    });
  }

  script.push({
    type: 'application/ld+json',
    innerHTML: JSON.stringify(
      buildBreadcrumbSchema({ items: breadcrumbItems })
    ),
    key: 'breadcrumb-schema',
  });

  return {
    script,
  };
});
</script>

<style>
@reference '../../../assets/css/tailwind.css';

.mb-tarifa__saldo {
  @apply border-r border-gray-300 px-8 py-12 flex flex-col items-center justify-center;
}

.mb-tarifa__saldo:last-of-type {
  @apply border-r-0;
}

.mb-tarifa__saldo span {
  @apply text-xs uppercase mb-4 block tracking-wider;
}

.mb-tarifa__saldo h3 {
  @apply text-2xl flex items-center justify-center;
}

.mb-tarifa__saldo h3 small {
  @apply mr-4;
}

.mb-tarifa__saldo svg {
  @apply text-gray-500;
}

.dark .mb-tarifa__saldo {
  @apply border-gray-700;
}

.mb-horarios__mi-saldo {
  @apply bg-orange-500/20 border-orange-500/30 text-orange-800 p-8 px-12 rounded-sm border mb-24 flex items-center;
}

.mb-horarios__mi-saldo span {
  @apply uppercase text-xs tracking-wider;
}

.mb-horarios__mi-saldo h4 {
  @apply font-semibold ml-auto;
}

.mb-horarios__mi-saldo button {
  @apply border-orange-500/30 ml-8 px-4;
}

.mb-horarios__mi-saldo svg {
  @apply ml-0;
}

.dark .mb-horarios__mi-saldo {
  @apply text-orange-100;
}

.mb-filtro__estaciones {
  @apply flex items-center justify-end mb-16;
}

.mb-filtro__estaciones span {
  @apply text-xs uppercase tracking-wider text-gray-800 mr-4;
}

.mb-filtro__estaciones button {
  @apply ml-4;
}

.mb-horarios__container {
  content-visibility: auto;
  contain-intrinsic-size: auto 640px;
  min-height: 320px;
}

@media (min-width: 640px) {
  .mb-horarios__container {
    contain-intrinsic-size: auto 420px;
    min-height: 220px;
  }
}

.dark .mb-filtro__estaciones span {
  @apply text-gray-300;
}
</style>
